import { useState } from 'react'
import { Banner, BlockStack, Button, InlineGrid, Page, Spinner, InlineStack, Text } from '@shopify/polaris'
import { useLocation } from 'react-router-dom'
import { useHealthReport } from '../hooks/useHealthReport'
import { ReadinessScoreCard } from '../components/ReadinessScoreCard'
import { WorkflowStatusCard } from '../components/WorkflowStatusCard'
import { ProductsHealthCard } from '../components/ProductsHealthCard'
import { CategoryHealthCard } from '../components/CategoryHealthCard'
import { OrdersHealthCard } from '../components/OrdersHealthCard'
import { FixPanel } from '../components/FixPanel'

type Props = {
  mode?:    'standalone' | 'wizard'
  onReady?: () => void
}

export function SystemHealthPage({ mode = 'standalone', onReady: _onReady }: Props) {
  const { report, loading, error, scanning, runScan, updateReport } = useHealthReport()
  const location = useLocation()
  const backAction = location.pathname.startsWith('/boarding/')
    ? { content: 'Boarding', url: '/boarding' }
    : undefined
  const [activeValidatorId, setActiveValidatorId] = useState<string | null>(null)
  const [auditKey, setAuditKey] = useState(0)

  function handleScan() { runScan(); setAuditKey(k => k + 1) }

  const productsCat = report?.categories.find(c => c.category === 'products')
  const ordersCat   = report?.categories.find(c => c.category === 'orders')
  const shippingCat = report?.categories.find(c => c.category === 'shipping')
  const financeCat  = report?.categories.find(c => c.category === 'finance')

  const cards = report && (
    <>
      <InlineGrid columns={{ xs: 1, sm: 2, lg: 3 }} gap="300">
        <ReadinessScoreCard report={report} />
        {shippingCat && <CategoryHealthCard cat={shippingCat} onViewIssues={setActiveValidatorId} />}
        {financeCat  && <CategoryHealthCard cat={financeCat}  onViewIssues={setActiveValidatorId} />}
      </InlineGrid>

      <InlineGrid columns={{ xs: 1, sm: 2, lg: 3 }} gap="300">
        <WorkflowStatusCard
          workflows={report.workflowReadiness}
          onViewIssues={setActiveValidatorId}
          refreshKey={auditKey}
        />
        <ProductsHealthCard
          cat={productsCat}
          onViewIssues={setActiveValidatorId}
          refreshKey={auditKey}
        />
        <OrdersHealthCard
          cat={ordersCat}
          onViewIssues={setActiveValidatorId}
          refreshKey={auditKey}
        />
      </InlineGrid>
    </>
  )

  const spinner = loading && !report && (
    <InlineStack align="center" blockAlign="center" gap="200">
      <Spinner accessibilityLabel="Running health scan" />
      <Text as="p" tone="subdued">Running initial health scan…</Text>
    </InlineStack>
  )

  const fixPanel = (
    <FixPanel
      validatorId={activeValidatorId}
      onClose={() => setActiveValidatorId(null)}
      onReportUpdated={updateReport}
    />
  )

  // ── Wizard mode: embedded in a parent page ────────────────────────────────
  if (mode === 'wizard') {
    return (
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="050">
            <Text as="h2" variant="headingMd">System Health</Text>
            <Text as="p" variant="bodySm" tone="subdued">Data quality and workflow readiness</Text>
          </BlockStack>
          <Button onClick={handleScan} loading={scanning}>Run Health Scan</Button>
        </InlineStack>

        {error  && <Banner tone="critical"><Text as="p">{error}</Text></Banner>}
        {spinner}
        {cards}
        {fixPanel}
        <div style={{ height: 48 }} />
      </BlockStack>
    )
  }

  // ── Standalone mode: full page ────────────────────────────────────────────
  return (
    <Page
      title="System Health"
      subtitle="Data quality and workflow readiness"
      primaryAction={{ content: 'Run Health Scan', onAction: handleScan, loading: scanning }}
      backAction={backAction}
    >
      <BlockStack gap="400">
        {error  && <Banner tone="critical"><Text as="p">{error}</Text></Banner>}
        {spinner}
        {cards}
        {fixPanel}
        <div style={{ height: 48 }} />
      </BlockStack>
    </Page>
  )
}
